<script setup lang="ts">
useHead({
  title: 'Refund Policy — Arvee Events',
  meta: [
    { name: 'robots', content: 'noindex, follow' },
    {
      name: 'description',
      content:
        'Refund Policy for Arvee Events — what happens to your money in every event scenario.',
    },
  ],
});
</script>

<template>
  <div>
    <h1 class="text-h4 mb-2">Refund Policy</h1>
    <p class="text-body-2 text-medium-emphasis mb-6">
      Last updated: March 2026
    </p>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">1. Overview</h2>
      <p class="text-body-1 mb-3">
        Arvee Events uses threshold-based funding. Events proceed only when
        enough participants commit. This means payments are captured at
        different times depending on the event phase, and refund rules vary by
        scenario.
      </p>
      <p class="text-body-1 mb-3">
        This policy explains what happens to your money in every case.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">2. Service Fees</h2>
      <p class="text-body-1 mb-3">
        Service fees are non-refundable in <strong>all</strong> circumstances —
        including event failure, organizer cancellation, force majeure, or any
        other scenario. Service fees apply to all events, including
        free-to-attend events.
      </p>
      <p class="text-body-1 mb-3">There are no exceptions.</p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">3. Organizer Listing Bond</h2>
      <p class="text-body-1 mb-3">
        Organizers pay a listing bond when submitting an event for review.
      </p>
      <p class="text-body-1 mb-3">
        The listing bond is returned to the organizer's original payment method
        <strong>only</strong> when the event reaches confirmation (threshold
        met, payments processed).
      </p>
      <p class="text-body-1 mb-3">
        The listing bond is retained by Arvee if the event is:
      </p>
      <ul class="text-body-1 mb-3 ml-6">
        <li>Denied during review</li>
        <li>Abandoned</li>
        <li>Fails to meet its threshold</li>
        <li>Canceled</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">4. Withdrawal During Fundraising</h2>
      <p class="text-body-1 mb-3">
        During the fundraising period (before the commitment deadline), your
        payment method is not charged. You have only authorized a future charge.
      </p>
      <p class="text-body-1 mb-3">
        If you withdraw your commitment before the deadline, no payment is
        captured and no refund is needed.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">5. Event Failure (Threshold Not Met)</h2>
      <p class="text-body-1 mb-3">
        If an event does not reach its minimum participant threshold, the event
        fails. During the payment processing window, some participants may have
        already been charged.
      </p>
      <p class="text-body-1 mb-3">If the event fails:</p>
      <ul class="text-body-1 mb-3 ml-6">
        <li>
          Ticket prices are refunded to the participant's original payment
          method
        </li>
        <li>Service fees are retained and not refunded</li>
      </ul>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">
        6. Organizer Cancellation Before Payment Processing
      </h2>
      <p class="text-body-1 mb-3">
        If the organizer cancels before the payment processing window begins
        (while fundraising is open and before the commitment deadline), no
        participant charges have been captured.
      </p>
      <p class="text-body-1 mb-3">
        No refund is needed because no money has been taken. The organizer's
        listing bond is retained.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">
        7. Organizer Cancellation During Payment Processing
      </h2>
      <p class="text-body-1 mb-3">
        Organizers cannot cancel an event while payment processing is active.
        This restriction exists to protect participants whose payments are being
        processed.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">8. Organizer Cancellation After Confirmation</h2>
      <p class="text-body-1 mb-3">
        When an organizer cancels a confirmed event, Arvee actively works to
        recover funds on behalf of attendees.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Recovery Process</h3>
      <p class="text-body-1 mb-3">
        Arvee reverses the transfer from the organizer's Stripe account. If
        funds have already been paid out to the organizer's bank, Stripe
        initiates a bank debit to recover the amount.
      </p>

      <h3 class="text-subtitle-1 font-weight-bold mb-2">Recovery Outcomes</h3>
      <ul class="text-body-1 mb-3 ml-6">
        <li>
          <strong>Full recovery:</strong> All ticket revenue recovered — full
          ticket price refunded to each attendee.
        </li>
        <li>
          <strong>Partial recovery:</strong> Some funds unrecoverable due to
          failed bank debit — each attendee receives a proportional refund.
        </li>
        <li>
          <strong>Zero recovery:</strong> In extreme edge cases, no funds may be
          recoverable.
        </li>
      </ul>
      <p class="text-body-1 mb-3">
        Service fees are never refunded, even with full recovery.
      </p>
      <p class="text-body-1 mb-3">
        <strong>Important:</strong> Full recovery is not guaranteed. The amount
        recovered depends on the organizer's Stripe account balance and bank
        account status.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">
        9. Post-Confirmation — No Attendee-Initiated Refunds
      </h2>
      <p class="text-body-1 mb-3">
        After an event is confirmed, attendees cannot request refunds through
        the platform. Once your payment is captured and the event is confirmed,
        that payment is final.
      </p>
      <p class="text-body-1 mb-3">
        If you cannot attend, any discretionary refund is the organizer's
        responsibility and is handled outside the platform.
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">10. Capacity and Sold-Out Events</h2>
      <p class="text-body-1 mb-3">
        Some events have limited ticket capacity. If an event sells out during
        payment processing, your commitment may not result in a ticket.
      </p>
      <p class="text-body-1 mb-3">
        In this case, you are not charged and no refund is needed. Commitments
        are processed in the order they were made (earliest first).
      </p>
    </section>

    <section class="mb-8">
      <h2 class="text-h5 mb-3">11. Force Majeure</h2>
      <p class="text-body-1 mb-3">
        Extraordinary events beyond reasonable control (natural disasters,
        government actions, severe weather, public health emergencies) do not
        automatically guarantee refunds.
      </p>
      <p class="text-body-1 mb-3">
        The applicable scenario from this policy determines the refund outcome.
      </p>
    </section>

    <section>
      <h2 class="text-h5 mb-3">12. Contact</h2>
      <p class="text-body-1 mb-3">
        Questions about refunds? Email us at
        <a href="mailto:support@arvee.io">support@arvee.io</a>.
      </p>
      <p class="text-body-1">
        Arvee Solutions, Inc.<br />
        2803 Philadelphia Pike Suite B-1290<br />
        Claymont, DE 19703
      </p>
    </section>
  </div>
</template>
